"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";

import { useOpenAuthorizationTracking } from "@/src/hooks/use-open-authorization-tracking";
import { toast } from "@/src/hooks/use-toast";
import { selectReferralAuthTrackingPriorAuthorization } from "@/src/lib/prior-authorization/referral-auth-prior-authorization-selection";
import type { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";

export function useReferralAuthOpenAuthorization({
  taskId,
  taskPriorAuthorization,
  linkedPriorAuthorizations,
}: {
  taskId: string;
  taskPriorAuthorization?: PriorAuthorization | null;
  linkedPriorAuthorizations: PriorAuthorization[];
}) {
  const router = useRouter();
  const { openAuthorizationTrackingSession, isOpening } = useOpenAuthorizationTracking();

  const handleOpenAuthorization = useCallback(async () => {
    const authorization = selectReferralAuthTrackingPriorAuthorization(
      linkedPriorAuthorizations,
      taskPriorAuthorization
    );

    if (!authorization?.displayId) {
      toast({
        title: "No authorization found",
        description: "Save authorization details from intake before opening the Authorizations tab.",
        variant: "destructive",
      });

      return;
    }

    const ok = await openAuthorizationTrackingSession({ authDisplayId: authorization.displayId, taskId });

    if (ok) {
      router.push(`/authorizations/${authorization.displayId}/comms`);
    }
  }, [linkedPriorAuthorizations, openAuthorizationTrackingSession, router, taskId, taskPriorAuthorization]);

  return { handleOpenAuthorization, isOpeningAuthorization: isOpening };
}
